
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useBoardContext } from '@/contexts/BoardContext';
import type { DragStateUser, DragPosition } from '@/contexts/DragStateContext';
import type { Tables } from '@/integrations/supabase/types';
import { TimeSync } from '@/utils/timeSync';

type CardType = Tables<'cards'>;

interface DragGhostCardProps {
  card: CardType;
  dragUser: DragStateUser;
  position: DragPosition;
  isOverColumn?: boolean;
  onCleanup?: () => void;
  isTerminating?: boolean; // **PHASE 1: Add termination awareness**
}

export function DragGhostCard({ 
  card, 
  dragUser, 
  position, 
  isOverColumn = false,
  onCleanup,
  isTerminating = false
}: DragGhostCardProps) {
  const { getTagName, getTagColor } = useBoardContext();
  
  // **PHASE 1: Enhanced component lifecycle management with termination handling**
  const [isVisible, setIsVisible] = useState(true);
  const [displayPosition, setDisplayPosition] = useState(position);
  const [fadeOut, setFadeOut] = useState(false); // **PHASE 1: Smooth fade out**
  const cleanupTimeoutRef = useRef<NodeJS.Timeout>();
  const componentId = useRef(`ghost-${dragUser.user_id}-${card.id}-${Date.now()}`).current;
  
  // **PHASE 1: Enhanced logging with termination context**
  const logger = useMemo(() => ({
    info: (message: string, data?: any) => {
      console.log(`👻 [GHOST ${componentId} ${isTerminating ? 'TERMINATING' : 'ACTIVE'}] ${message}`, data || '');
    },
    warning: (message: string, data?: any) => {
      console.warn(`⚠️ [GHOST ${componentId} ${isTerminating ? 'TERMINATING' : 'ACTIVE'}] ${message}`, data || '');
    },
    success: (message: string, data?: any) => {
      console.log(`✅ [GHOST ${componentId}] ${message}`, data || '');
    }
  }), [componentId, isTerminating]);
  
  // Stable component key based on props
  const stableKey = useMemo(() => 
    `ghost-${dragUser.user_id}-${card.id}`, 
    [dragUser.user_id, card.id]
  );
  
  // Stable window dimensions with useRef
  const windowDimensionsRef = useRef({ width: 0, height: 0 });
  
  // Update window dimensions safely
  useEffect(() => {
    const updateDimensions = () => {
      windowDimensionsRef.current = {
        width: window.innerWidth,
        height: window.innerHeight
      };
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);
  
  // Direct position updates
  useEffect(() => {
    setDisplayPosition(position);
    logger.info('Position updated', { position });
  }, [position, logger]);
  
  // **PHASE 1: Enhanced cleanup with termination handling**
  const triggerCleanup = useMemo(() => {
    return (reason: string = 'unknown') => {
      logger.info(`Triggering cleanup: ${reason}`);
      
      // **PHASE 1: Smooth fade out for termination**
      if (isTerminating || reason === 'termination') {
        setFadeOut(true);
        setTimeout(() => {
          setIsVisible(false);
          onCleanup?.();
        }, 200); // Quick fade out
      } else {
        setIsVisible(false);
        onCleanup?.();
      }
    };
  }, [onCleanup, logger, isTerminating]);
  
  // **PHASE 1: Termination-aware lifecycle effect**
  useEffect(() => {
    logger.info('Ghost card mounted', {
      user: dragUser.user_name,
      cardId: card.id,
      isOverColumn,
      isTerminating
    });
    
    // **PHASE 1: Shorter timeout for terminating cards**
    const timeout = isTerminating ? 1000 : 3000;
    
    cleanupTimeoutRef.current = setTimeout(() => {
      logger.warning(`Auto-hiding ghost card after ${timeout}ms timeout`);
      triggerCleanup('timeout');
    }, timeout);
    
    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      logger.info('Ghost card unmounted');
    };
  }, [dragUser.user_name, card.id, isOverColumn, triggerCleanup, logger, isTerminating]);
  
  // **PHASE 1: Enhanced staleness check with termination awareness**
  useEffect(() => {
    if (!dragUser.updated_at) return;
    
    const checkStale = () => {
      const age = TimeSync.getAge(dragUser.updated_at);
      const staleThreshold = isTerminating ? 1500 : 3000; // Faster cleanup for terminating
      
      if (age > staleThreshold) {
        logger.warning('Hiding stale ghost card', {
          user: dragUser.user_name,
          cardId: card.id,
          age: `${Math.round(age / 1000)}s`,
          isTerminating
        });
        triggerCleanup('stale');
      }
    };
    
    const intervalId = setInterval(checkStale, 1000);
    return () => clearInterval(intervalId);
  }, [dragUser.updated_at, dragUser.user_name, card.id, triggerCleanup, logger, isTerminating]);
  
  // Monitor drag user active state
  useEffect(() => {
    if (!dragUser.is_active) {
      logger.info('Drag user became inactive, cleaning up');
      triggerCleanup('inactive');
    }
  }, [dragUser.is_active, triggerCleanup, logger]);
  
  // Early return for invisible components
  if (!isVisible) {
    return null;
  }

  // Stable positioning calculations with defensive checks
  const cardDimensions = useMemo(() => ({
    width: 280,
    height: 130
  }), []);

  const adjustedPosition = useMemo(() => {
    if (!displayPosition) {
      return { x: 0, y: 0 };
    }
    
    const { width, height } = cardDimensions;
    const padding = 12;
    const { width: vWidth, height: vHeight } = windowDimensionsRef.current;
    
    // Fallback to safe defaults if window dimensions not available
    const viewportWidth = vWidth || 1024;
    const viewportHeight = vHeight || 768;
    
    const offsetX = width / 2;
    const offsetY = height / 2;
    
    let x = displayPosition.x - offsetX;
    let y = displayPosition.y - offsetY;
    
    // Constrain to viewport with stable calculations
    x = Math.max(padding, Math.min(x, viewportWidth - width - padding));
    y = Math.max(padding, Math.min(y, viewportHeight - height - padding));
    
    return { x, y };
  }, [displayPosition, cardDimensions]);

  // **PHASE 1: Enhanced visual effects with termination states**
  const visualEffects = useMemo(() => {
    const baseScale = isOverColumn ? 1.03 : 0.97;
    const baseOpacity = isOverColumn ? 0.95 : 0.85;
    
    // **PHASE 1: Adjust for terminating state**
    const scale = isTerminating ? baseScale * 0.95 : baseScale;
    const opacity = isTerminating || fadeOut ? baseOpacity * 0.5 : baseOpacity;
    
    const borderColor = isTerminating 
      ? "border-orange-400 dark:border-orange-500"
      : isOverColumn 
        ? "border-green-400 dark:border-green-500" 
        : "border-blue-400 dark:border-blue-500";
        
    const bgColor = isTerminating
      ? "bg-white/70 dark:bg-slate-700/70"
      : isOverColumn 
        ? "bg-white/95 dark:bg-slate-700/95" 
        : "bg-white/90 dark:bg-slate-700/90";
    
    return {
      scale,
      opacity,
      borderColor,
      bgColor,
      shadow: `0 ${isOverColumn ? '20px 40px' : '15px 30px'} -12px rgba(0, 0, 0, ${isTerminating ? '0.15' : '0.3'}), 0 0 ${isOverColumn ? '15px' : '8px'} rgba(59, 130, 246, ${isTerminating ? '0.2' : '0.4'})`
    };
  }, [isOverColumn, isTerminating, fadeOut]);

  return (
    <div
      key={stableKey}
      className={cn(
        "fixed pointer-events-none z-50 transition-all duration-200 ease-out",
        fadeOut && "transition-opacity duration-200" // **PHASE 1: Smooth fade transition**
      )}
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        transform: `scale(${visualEffects.scale})`,
        opacity: visualEffects.opacity,
        width: cardDimensions.width,
      }}
    >
      {/* **PHASE 1: Enhanced user indicator with termination status** */}
      <div className="absolute -top-8 left-0 flex items-center gap-2 z-10">
        <Avatar className={cn(
          "h-5 w-5 border-2 shadow-md transition-colors duration-200",
          isTerminating
            ? "border-orange-500"
            : isOverColumn 
              ? "border-green-500" 
              : "border-blue-500"
        )}>
          <AvatarFallback className={cn(
            "text-white text-xs font-bold",
            isTerminating
              ? "bg-orange-500"
              : isOverColumn 
                ? "bg-green-500" 
                : "bg-blue-500"
          )}>
            {(dragUser.user_name ?? "?").slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <Badge 
          variant="secondary" 
          className={cn(
            "text-xs whitespace-nowrap transition-colors duration-200",
            isTerminating
              ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
              : isOverColumn 
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" 
                : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
          )}
        >
          {isTerminating 
            ? `${dragUser.user_name} finishing...`
            : isOverColumn 
              ? `${dragUser.user_name} dropping...` 
              : `${dragUser.user_name} dragging`}
        </Badge>
      </div>

      {/* Enhanced ghost card with termination awareness */}
      <Card 
        className={cn(
          "border-2 border-dashed relative overflow-hidden transition-all duration-200",
          visualEffects.borderColor,
          visualEffects.bgColor,
          "backdrop-blur-sm",
          isTerminating && "animate-pulse" // **PHASE 1: Subtle pulse for terminating**
        )}
        style={{
          boxShadow: visualEffects.shadow,
        }}
      >        
        <div className="relative z-10">
          <CardHeader className="pb-2">
            {/* Simplified tags - limit to 2 for stability */}
            {card.tags && card.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {card.tags.slice(0, 2).map((tag, index) => (
                  <Badge
                    key={`${stableKey}-tag-${index}`}
                    variant="secondary"
                    className={cn(
                      "text-xs transition-opacity duration-200",
                      isTerminating ? "opacity-60" : "opacity-80"
                    )}
                    style={{
                      backgroundColor: getTagColor(tag),
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    {getTagName(tag)}
                  </Badge>
                ))}
                {card.tags.length > 2 && (
                  <Badge variant="secondary" className={cn(
                    "text-xs",
                    isTerminating ? "opacity-40" : "opacity-60"
                  )}>
                    +{card.tags.length - 2}
                  </Badge>
                )}
              </div>
            )}
            
            <h4 className={cn(
              "font-medium leading-tight text-gray-900 dark:text-slate-100 line-clamp-2 transition-opacity duration-200",
              isTerminating && "opacity-70"
            )}>
              {card.title}
            </h4>
            
            {/* Simplified description */}
            {card.description && card.description.length < 50 && (
              <p className={cn(
                "text-sm text-gray-600 dark:text-slate-300 line-clamp-1 mt-1 transition-opacity duration-200",
                isTerminating && "opacity-60"
              )}>
                {card.description}
              </p>
            )}
          </CardHeader>
          
          <CardContent className="pt-0 space-y-2">
            <div className="flex items-center justify-between">
              {card.category && (
                <Badge variant="outline" className={cn(
                  "text-xs w-fit transition-opacity duration-200",
                  isTerminating ? "opacity-50" : "opacity-70"
                )}>
                  {card.category}
                </Badge>
              )}
              
              {/* **PHASE 1: Enhanced drop indicator with termination status** */}
              {(isOverColumn || isTerminating) && (
                <Badge 
                  variant="default" 
                  className={cn(
                    "text-xs shadow-md transition-colors duration-200",
                    isTerminating
                      ? "bg-orange-500 text-white"
                      : "bg-green-500 text-white"
                  )}
                >
                  {isTerminating ? "Finishing..." : "Dropping ✓"}
                </Badge>
              )}
            </div>
          </CardContent>
        </div>
      </Card>
    </div>
  );
}
